import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "../components/shared";
import { SiteLayout, PageHero, CtaBand, BookBtn, ReferBtn, Caps, H2 } from "../components/site";
import { CATEGORIES, FORMATS } from "../content";
import { TEAM } from "../team";
import { EnquiryBtn } from "../components/enquiry";

const byId = Object.fromEntries(TEAM.map((m) => [m.id, m]));

const ENQUIRE_TOPIC = { care: "Care navigation & assessments", professional: "Professional services", "family-reports": "Family reports & court assessments" };

const BookThis = ({ c }) => ENQUIRE_TOPIC[c.id] ? (
  <EnquiryBtn topic={ENQUIRE_TOPIC[c.id]} testid={`category-${c.id}-enquire`} className="!py-3" />
) : (
  <Link to="/book#book-types" data-testid={`category-${c.id}-book`} className="group inline-flex items-center gap-2 rounded-full bg-[#1F4D3A] px-6 py-3 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#2C6B50] transition-colors">
    Book this service <ArrowUpRight className="w-4 h-4 text-[#F3DCA8] transition-transform duration-500 group-hover:rotate-45" />
  </Link>
);

export const CategoryCards = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="category-cards">
    {CATEGORIES.map((c, i) => (
      <Reveal key={c.id} delay={(i % 3) * 0.08} className={i === 0 ? "lg:row-span-2" : ""}>
        <Link to={`/services#${c.id}`} data-testid={`category-card-${c.id}`} className={`group relative flex flex-col justify-end overflow-hidden rounded-[28px] bg-[#E3E9E4] p-7 ${i === 0 ? "h-[360px] lg:h-full" : "h-[260px]"}`}>
          <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A4232]/85 via-[#0A4232]/30 to-transparent" />
          <div className="relative text-[#F6F2E9]">
            <c.icon className="w-7 h-7 mb-4" strokeWidth={1.3} />
            <h3 className="font-newsreader text-3xl">{c.title}</h3>
            <p className="mt-2 font-jakarta text-sm text-[#EEEADF] max-w-xs">{c.blurb}</p>
          </div>
          <span className="absolute top-6 right-6 w-11 h-11 rounded-full bg-[#F6F2E9]/90 grid place-items-center text-[#3F4A3C] group-hover:rotate-45 transition-transform duration-500"><ArrowUpRight className="w-4 h-4" /></span>
        </Link>
      </Reveal>
    ))}
  </div>
);

const Category = ({ c, flip }) => (
  <section id={c.id} className="scroll-mt-24 py-14 sm:py-20 border-t border-[#3F4A3C]/10" data-testid={`service-category-${c.id}`}>
    <div className="max-w-[1360px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-10 items-center">
      <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <div className={`flex ${flip ? "lg:justify-end" : "lg:justify-start"} justify-center items-center h-56 sm:h-72 lg:h-[24rem]`} data-testid={`service-arch-${c.id}`}>
          <div className="relative h-full aspect-square max-w-full">
            <div className="relative h-full w-full rounded-t-full rounded-b-[28px] overflow-hidden border border-[#C59B45]/30 shadow-[0_30px_60px_-35px_rgba(31,77,58,0.45)]">
              <img src={`/images/arches/${c.id}.jpg`} alt={`${c.title} illustration`} loading="lazy"
                className="select-none w-full h-full object-cover scale-[1.04]" />
            </div>
          </div>
        </div>
      </Reveal>
      <Reveal className="lg:col-span-7" delay={0.1}>
        <c.icon className="hidden sm:block w-8 h-8 text-[#5F7C8A]" strokeWidth={1.3} />
        <H2 className="sm:mt-4">{c.title}</H2>
        <p className="mt-4 font-jakarta text-base text-[#4A4F45] max-w-lg">{c.blurb}</p>
        <ul className="mt-6 grid sm:grid-cols-2 gap-3">
          {c.items.map((it) => <li key={it} className="flex gap-3 rounded-2xl bg-[#E3E9E4] px-4 py-3 font-jakarta text-sm text-[#3F4A3C]"><Check className="hidden sm:block w-4 h-4 mt-0.5 text-[#5F7C8A] shrink-0" />{it}</li>)}
        </ul>
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid={`category-${c.id}-actions`}>
        <BookThis c={c} />
        {c.id === "professional" && <Link to="/eap" data-testid="category-professional-eap-link" className="group inline-flex items-center gap-2 rounded-full bg-[#3F4A3C] px-6 py-3 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors">Employee Assistance Program (EAP) <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" /></Link>}
        {c.link && <Link to={c.link.to} data-testid={`category-${c.id}-link`} className="group inline-flex items-center gap-2 rounded-full bg-[#3F4A3C] px-6 py-3 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors">{c.link.label} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" /></Link>}
        </div>
        <div className="mt-8"><Caps>Practitioners</Caps>
          <div className="mt-4 flex flex-wrap gap-3">
            {c.team.map((id) => byId[id] && (
              <Link key={id} to={`/team#${id}`} data-testid={`category-${c.id}-member-${id}`} className="group flex items-center gap-3 rounded-full bg-[#F9F6EF] border border-[#3F4A3C]/10 pr-4 hover:border-[#3F4A3C]/30 transition-colors">
                <img src={`/images/team/${id}.jpg`} alt={byId[id].name} className="w-11 h-11 rounded-full object-cover object-top" />
                <span className="font-jakarta text-sm text-[#3F4A3C]">{byId[id].name.split(" ")[0]}</span>
              </Link>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Services() {
  return (
    <SiteLayout testid="services-page">
      <PageHero eyebrow="Our services" lines={["Many disciplines.", "One caring team."]} sub={`For all ages. Available ${FORMATS.join(", ").toLowerCase()}.`}>
        <BookBtn testid="services-book" /><ReferBtn testid="services-refer" />
      </PageHero>
      {CATEGORIES.map((c, i) => <Category key={c.id} c={c} flip={i % 2 === 1} />)}
      <CtaBand />
    </SiteLayout>
  );
}
